import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { projectsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import ProjectStatusLamps from "../components/ProjectStatusLamps";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SortableTableHead, useTableSort } from "../components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Search, FolderKanban, Eye, User, Archive, ArchiveRestore, Calendar } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const STATUS_OPTIONS = [
  { value: "all", label: "Alle Status" },
  { value: "neu", label: "Neu" },
  { value: "angebot_erstellt", label: "Angebot erstellt" },
  { value: "angebot_angenommen", label: "Angebot angenommen" },
  { value: "angebot_abgelehnt", label: "Angebot abgelehnt" },
  { value: "in_planung", label: "In Planung" },
  { value: "installation", label: "Installation" },
  { value: "abgeschlossen", label: "Abgeschlossen" },
  { value: "bezahlt", label: "Bezahlt" },
];

const STATUS_COLORS = {
  neu: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  angebot_erstellt: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  angebot_angenommen: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  angebot_abgelehnt: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  in_planung: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  installation: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  abgeschlossen: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  bezahlt: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  
  // Sorting state - default to newest first
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort("created_at", "desc");

  useEffect(() => {
    loadProjects();
  }, [showArchived]);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.list(null, showArchived);
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to load projects:", error);
      toast.error("Fehler beim Laden der Projekte");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (projectId) => {
    if (!window.confirm("Projekt wirklich archivieren?")) return;
    try {
      await projectsAPI.archive(projectId);
      toast.success("Projekt archiviert");
      loadProjects();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleUnarchive = async (projectId) => {
    try {
      await projectsAPI.unarchive(projectId);
      toast.success("Projekt wiederhergestellt");
      loadProjects();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  const getStatusLabel = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option ? option.label : status;
  };

  const filteredProjects = projects.filter(project => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      project.project_number?.toLowerCase().includes(searchLower) ||
      project.customer_name?.toLowerCase().includes(searchLower) ||
      project.created_by_name?.toLowerCase().includes(searchLower);
    
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Apply sorting
  const sortedProjects = sortData(filteredProjects);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] flex items-center gap-2">
            <FolderKanban className="h-8 w-8" />
            Projekte
          </h1>
          <p className="text-muted-foreground">
            Übersicht aller Projekte
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suche nach Projekt, Kunde, Vertriebler..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
              data-testid="project-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px] h-12">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
            Archivierte anzeigen
          </Label>
        </div>
      </div>

      {/* Projects Table */}
      <Card>
        <CardContent className="p-0">
          {sortedProjects.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    column="project_number"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Projektnr.
                  </SortableTableHead>
                  <SortableTableHead
                    column="customer_name"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Kunde
                  </SortableTableHead>
                  <SortableTableHead
                    column="status"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  >
                    Status
                  </SortableTableHead>
                  <TableHead className="hidden lg:table-cell">Erstellt von</TableHead>
                  <SortableTableHead
                    column="created_at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  >
                    Erstellt am
                  </SortableTableHead>
                  <TableHead className="hidden lg:table-cell">Installation</TableHead>
                  <TableHead className="hidden md:table-cell">Meldungen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProjects.map((project) => (
                  <TableRow key={project.id} className={project.archived ? "opacity-60" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{project.project_number}</span>
                        {project.archived && (
                          <Badge variant="outline" className="text-xs">
                            Archiviert
                          </Badge>
                        )}
                        <IncognitoBadge active={project.is_incognito} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link 
                        to={`/kunden/${project.customer_id}`}
                        className="hover:underline font-medium"
                      >
                        {project.customer_name}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge className={STATUS_COLORS[project.status] || "bg-gray-100"}>
                        {getStatusLabel(project.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1.5 text-sm">
                        {project.created_by_name ? (
                          <>
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{project.created_by_name}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {project.created_at ? format(new Date(project.created_at), "dd.MM.yyyy", { locale: de }) : "-"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {project.installation_date ? (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          {format(new Date(project.installation_date), "dd.MM.yyyy", { locale: de })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <ProjectStatusLamps project={project} size="compact" />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          title="Projekt öffnen"
                        >
                          <Link to={`/projekte/${project.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        {!project.archived ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchive(project.id)}
                            title="Archivieren"
                          >
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUnarchive(project.id)}
                            title="Wiederherstellen"
                          >
                            <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FolderKanban className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Projekte gefunden</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter !== "all" 
                  ? "Versuchen Sie andere Suchkriterien"
                  : "Es wurden noch keine Projekte angelegt"
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {filteredProjects.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {filteredProjects.length} Projekt{filteredProjects.length !== 1 ? "e" : ""} gefunden
        </p>
      )}
    </div>
  );
}
